import React, { useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertTriangle, CheckCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { tokenCoordinator } from '@/services/tokenCoordinationService';
import { tokenMonitor } from '@/services/tokenMonitoringService';

interface TokenErrorBoundaryProps {
  children: React.ReactNode;
  fallbackMessage?: string;
  showRetryButton?: boolean;
  autoRetryAttempts?: number;
}

interface TokenErrorState {
  hasError: boolean;
  errorType: 'permission-denied' | 'throttled' | 'network' | 'unknown';
  errorMessage: string;
  retryCount: number;
  isRetrying: boolean;
  lastErrorTime: number;
}

export const TokenErrorBoundary: React.FC<TokenErrorBoundaryProps> = ({ 
  children, 
  fallbackMessage = "Your session has expired or encountered an authentication issue.",
  showRetryButton = true,
  autoRetryAttempts = 2
}) => {
  const { currentUser } = useAuth();
  const [errorState, setErrorState] = useState<TokenErrorState>({
    hasError: false,
    errorType: 'unknown',
    errorMessage: '',
    retryCount: 0,
    isRetrying: false,
    lastErrorTime: 0
  });

  // Reset error state when user changes
  useEffect(() => {
    if (currentUser) {
      setErrorState(prev => ({ ...prev, hasError: false, retryCount: 0 }));
    }
  }, [currentUser]);

  // Listen for permission denied and other token-related errors
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      const error = event.error;
      if (!error) return;

      let errorType: TokenErrorState['errorType'] = 'unknown';
      let shouldHandle = false;

      // Check for permission denied errors
      if (error.code === 'permission-denied' || 
          error.message?.includes('permission-denied') ||
          error.message?.includes('Missing or insufficient permissions')) {
        errorType = 'permission-denied';
        shouldHandle = true;
      }
      // Check for App Check throttling
      else if (error.code === 'appCheck/throttled' || 
               error.message?.includes('throttled')) {
        errorType = 'throttled';
        shouldHandle = true;
      }
      // Check for network errors
      else if (error.code === 'auth/network-request-failed' ||
               error.message?.includes('network')) {
        errorType = 'network';
        shouldHandle = true;
      }

      if (shouldHandle) {
        console.log('[TokenErrorBoundary] Caught token-related error:', { errorType, error });
        // Sentry's own GlobalHandlers integration (on by default) ALSO listens
        // on window 'error'/'unhandledrejection' and will independently
        // capture this same error, untagged. This manual call is deliberately
        // duplicative — it's what attaches the errorType/source tags that let
        // these be filtered in the Sentry UI; the two typically group into one
        // issue since they share the same stack.
        Sentry.captureException(error, { tags: { source: 'TokenErrorBoundary', errorType } });

        setErrorState(prev => ({
          hasError: true,
          errorType,
          errorMessage: error.message || error.toString(),
          retryCount: prev.retryCount,
          isRetrying: false,
          lastErrorTime: Date.now()
        }));

        // Auto-retry for certain error types if we haven't exceeded attempts
        if (errorType === 'permission-denied' && errorState.retryCount < autoRetryAttempts) {
          setTimeout(() => {
            handleAutoRetry();
          }, 2000); // Wait 2 seconds before auto-retry
        }
      }
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      if (!error) return;

      // Handle Firebase errors that might not be caught by the error handler
      if (error.code === 'permission-denied' ||
          error.message?.includes('permission-denied')) {
        console.log('[TokenErrorBoundary] Caught unhandled permission denied:', error);
        // See the handleError capture above — same deliberate overlap with
        // Sentry's GlobalHandlers integration.
        Sentry.captureException(error, { tags: { source: 'TokenErrorBoundary', errorType: 'permission-denied' } });

        setErrorState(prev => ({
          hasError: true,
          errorType: 'permission-denied',
          errorMessage: error.message || error.toString(),
          retryCount: prev.retryCount,
          isRetrying: false,
          lastErrorTime: Date.now()
        }));
      }
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, [autoRetryAttempts]);

  const handleAutoRetry = useCallback(async () => {
    if (errorState.isRetrying) return;

    console.log('[TokenErrorBoundary] Attempting auto-retry for token error');
    setErrorState(prev => ({ ...prev, isRetrying: true }));

    try {
      const success = await tokenCoordinator.coordinatedTokenRefresh(currentUser);
      
      if (success) {
        console.log('[TokenErrorBoundary] Auto-retry successful, clearing error state');
        setErrorState(prev => ({
          hasError: false,
          errorType: 'unknown',
          errorMessage: '',
          retryCount: 0,
          isRetrying: false,
          lastErrorTime: 0
        }));
      } else {
        console.log('[TokenErrorBoundary] Auto-retry failed, incrementing retry count');
        setErrorState(prev => ({
          ...prev,
          retryCount: prev.retryCount + 1,
          isRetrying: false
        }));
      }
    } catch (error) {
      console.error('[TokenErrorBoundary] Auto-retry error:', error);
      setErrorState(prev => ({
        ...prev,
        retryCount: prev.retryCount + 1,
        isRetrying: false
      }));
    }
  }, [currentUser, errorState.isRetrying]);

  const handleManualRetry = useCallback(async () => {
    if (errorState.isRetrying) return;

    console.log('[TokenErrorBoundary] Manual retry requested');
    setErrorState(prev => ({ ...prev, isRetrying: true }));

    try {
      // Force a token refresh
      const success = await tokenCoordinator.coordinatedTokenRefresh(currentUser);
      
      if (success) {
        // Also trigger emergency token monitoring check
        await tokenMonitor.emergencyTokenRefresh();
        
        console.log('[TokenErrorBoundary] Manual retry successful');
        setErrorState({
          hasError: false,
          errorType: 'unknown',
          errorMessage: '',
          retryCount: 0,
          isRetrying: false,
          lastErrorTime: 0
        });
      } else {
        console.log('[TokenErrorBoundary] Manual retry failed');
        setErrorState(prev => ({
          ...prev,
          isRetrying: false
        }));
      }
    } catch (error) {
      console.error('[TokenErrorBoundary] Manual retry error:', error);
      setErrorState(prev => ({
        ...prev,
        isRetrying: false
      }));
    }
  }, [currentUser, errorState.isRetrying]);

  const handleRefreshPage = () => {
    console.log('[TokenErrorBoundary] Page refresh requested');
    window.location.reload();
  };

  const getErrorIcon = () => {
    if (errorState.isRetrying) return <RefreshCw className="h-4 w-4 animate-spin" />;
    if (errorState.errorType === 'throttled') return <AlertTriangle className="h-4 w-4 text-orange-500" />;
    return <AlertTriangle className="h-4 w-4 text-red-500" />;
  };

  const getErrorTitle = () => {
    switch (errorState.errorType) {
      case 'permission-denied':
        return 'Session Expired';
      case 'throttled':
        return 'Too Many Requests';
      case 'network':
        return 'Network Error';
      default:
        return 'Authentication Error';
    }
  };

  const getErrorDescription = () => {
    switch (errorState.errorType) {
      case 'permission-denied':
        return 'Your session has expired. Please refresh your authentication to continue.';
      case 'throttled':
        return 'Too many requests have been made. Please wait a moment before trying again.';
      case 'network':
        return 'A network error occurred. Please check your connection and try again.';
      default:
        return fallbackMessage;
    }
  };

  if (errorState.hasError) {
    return (
      <div className="p-4 max-w-2xl mx-auto">
        <Alert className="mb-4">
          <div className="flex items-center gap-2">
            {getErrorIcon()}
            <div className="flex-1">
              <h3 className="font-semibold">{getErrorTitle()}</h3>
              <AlertDescription className="mt-1">
                {getErrorDescription()}
              </AlertDescription>
              
              {errorState.retryCount > 0 && (
                <div className="mt-2 text-sm text-muted-foreground">
                  Retry attempts: {errorState.retryCount}/{autoRetryAttempts}
                </div>
              )}
            </div>
          </div>
          
          {showRetryButton && (
            <div className="flex gap-2 mt-4">
              <Button 
                onClick={handleManualRetry} 
                disabled={errorState.isRetrying}
                size="sm"
                variant="outline"
              >
                {errorState.isRetrying ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Retry Authentication
                  </>
                )}
              </Button>
              
              <Button 
                onClick={handleRefreshPage} 
                size="sm"
                variant="default"
              >
                Refresh Page
              </Button>
            </div>
          )}
        </Alert>

        {/* Show monitoring status for debugging */}
        {process.env.NODE_ENV === 'development' && (
          <Alert className="mt-4">
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="text-sm">
                <strong>Debug Info:</strong>
                <pre className="mt-1 text-xs">
                  {JSON.stringify({
                    errorType: errorState.errorType,
                    retryCount: errorState.retryCount,
                    isRetrying: errorState.isRetrying,
                    monitoringStatus: tokenMonitor.getStatus(),
                    coordinatorStatus: tokenCoordinator.getRefreshStatus()
                  }, null, 2)}
                </pre>
              </div>
            </AlertDescription>
          </Alert>
        )}
      </div>
    );
  }

  return <>{children}</>;
};
